import apiClient from '../client'

// Scout configuration response
export interface ScoutConfig {
  wait_days: number
  min_quality_score?: number
  last_run?: string
  last_run_stats?: {
    processed: number
    created: number
    duplicates: number
    rejected: number
  }
}

// Scout run stats response
export interface ScoutRunStats {
  total_processed?: number
  total_created?: number
  total_duplicates?: number
  total_rejected?: number
  processed?: number
  created?: number
  duplicates?: number
  rejected?: number
}

// Scout stats for dashboard display (matches backend ScoutStatsResponse)
export interface ScoutStats {
  total_leads: number
  leads_today: number
  leads_this_week: number
  leads_by_source: Record<string, number>
  leads_by_priority: Record<string, number>
  average_quality_score: number
  enrichment_rate: number
}

// Combined stats with config for dashboard
export interface ScoutDashboardStats extends ScoutStats {
  config?: ScoutConfig
  lastRun?: {
    time: string | null
    outcome: string | null
    stats: ScoutRunStats
    trigger?: string
    source?: string
  }
}

// Run history response from backend
export interface RunHistoryResponse {
  success: boolean
  runs: Array<{
    id: number
    activity_type: string
    subject: string | null
    notes: string | null
    outcome: string
    created_at: string
    stats: ScoutRunStats
    trigger?: string
    source?: string
  }>
  total_runs: number
  runs_last_24h: number
  last_run: {
    time: string | null
    outcome: string | null
    stats: ScoutRunStats
    trigger?: string
    source?: string
  } | null
}

// Scheduler job from backend
export interface SchedulerJob {
  name: string
  full_name: string
  description: string
  schedule: string
  time_zone: string
  state: 'ENABLED' | 'PAUSED' | 'DISABLED'
  uri: string
  http_method: string
  next_run?: string
  last_run?: string
  last_status_code?: number
}

// Scheduler jobs response
export interface SchedulerJobsResponse {
  success: boolean
  project: string
  location: string
  jobs: SchedulerJob[]
  count: number
}

// Job results response
export interface JobResultsResponse {
  success: boolean
  job_name: string
  last_run: string | null
  data: {
    type: string
    // Riviera scout fields
    wait_days?: number
    total_runs?: number
    runs_last_24h?: number
    runs_last_period?: number
    total_leads_created?: number
    leads_last_24h?: number
    leads_last_period?: number
    last_run_time?: string
    recent_runs?: Array<{
      id: string
      created_at: string
      outcome: string
      source_type: string
      leads_created: number
      leads_processed: number
      duplicates: number
      rejected: number
      errors: number
    }>
    // FMCSA download fields
    data_directory?: string
    carrier_files?: string[]
    insurance_files?: string[]
    latest_carrier?: { file: string; modified: string; size: number }
    latest_insurance?: { file: string; modified: string; size: number }
    total_carrier_files?: number
    total_insurance_files?: number
  }
}

// Run scout request
export interface RunScoutRequest {
  wait_days?: number
  min_quality_score?: number
  date_start?: string
  date_end?: string
  source_type?: string
}

// Preview carrier item
export interface PreviewCarrier {
  mc_number: string | null
  company_name: string | null
  phone: string | null
  state: string | null
  city: string | null
  zip_code: string | null
  sales_rep_name: string | null
  sales_rep_email: string | null
}

// Preview response
export interface PreviewResponse {
  success: boolean
  total: number
  duplicates_removed: number
  date_start: string | null
  date_end: string | null
  source_type: string
  carriers: PreviewCarrier[]
}

// Ingest lead request
export interface IngestLeadRequest {
  company_name?: string
  mc_number?: string
  dot_number?: string
  phone?: string
  email?: string
  city?: string
  state?: string
  zip_code?: string
  source?: string
}

// Backend response type for config endpoint
interface ConfigResponse {
  success: boolean
  config: {
    wait_days: number
    description?: string
    source?: string
  }
}

export const scoutApi = {
  // Get scout configuration
  getConfig: async () => {
    const response = await apiClient.get<ConfigResponse>('/api/scout/riviera/config')
    // Extract config from nested response structure
    const configData = response.data.config || response.data
    return {
      ...response,
      data: {
        wait_days: configData.wait_days,
        // Include other ScoutConfig fields if present
      } as ScoutConfig
    }
  },

  // Update scout configuration
  updateConfig: (config: Partial<ScoutConfig>) =>
    apiClient.put<{ success: boolean; config: ScoutConfig }>('/api/scout/riviera/config', config),

  // Get scout stats for dashboard - uses the dedicated scout stats endpoint
  getStats: async () => {
    // Fetch stats, config, and run history in parallel
    const [statsRes, configRes, runHistoryRes] = await Promise.all([
      apiClient.get<ScoutStats>('/api/scout/stats').catch(() => ({
        data: {
          total_leads: 0,
          leads_today: 0,
          leads_this_week: 0,
          leads_by_source: {},
          leads_by_priority: {},
          average_quality_score: 0,
          enrichment_rate: 0,
        } as ScoutStats
      })),
      apiClient.get<ConfigResponse>('/api/scout/riviera/config').catch(() => ({
        data: { success: false, config: { wait_days: 7 } } as ConfigResponse
      })),
      apiClient.get<RunHistoryResponse>('/api/scout/run-history?limit=1').catch(() => ({
        data: { success: false, runs: [], total_runs: 0, runs_last_24h: 0, last_run: null } as RunHistoryResponse
      })),
    ])

    // Extract config from nested response structure
    const configData = configRes.data.config || configRes.data

    const stats: ScoutDashboardStats = {
      ...statsRes.data,
      config: {
        wait_days: configData.wait_days,
      } as ScoutConfig,
      lastRun: runHistoryRes.data.last_run || undefined,
    }

    return { data: stats }
  },

  // Run scout - all carriers
  runScout: (params?: RunScoutRequest) =>
    apiClient.post<{ success: boolean; stats: ScoutRunStats }>('/api/scout/riviera/scout', params || {}),

  // Run scout - newly applied carriers only
  runScoutNewlyApplied: (params?: RunScoutRequest) =>
    apiClient.post<{ success: boolean; stats: ScoutRunStats }>('/api/scout/riviera/scout/newly-applied', params || {}),

  // Run scout - newly approved carriers only
  runScoutNewlyApproved: (params?: RunScoutRequest) =>
    apiClient.post<{ success: boolean; stats: ScoutRunStats }>('/api/scout/riviera/scout/newly-approved', params || {}),

  // Preview carriers without ingesting
  previewScout: (params?: RunScoutRequest) =>
    apiClient.post<PreviewResponse>('/api/scout/riviera/scout/preview', params || {}),

  // Legacy alias for triggerDiscovery
  triggerDiscovery: async (source: string, _maxResults?: number) => {
    // Map to the correct scout endpoint based on source
    // maxResults is accepted for compatibility but not used in Riviera scout
    const params = { min_quality_score: 0 }

    if (source === 'fmcsa' || source === 'both') {
      return scoutApi.runScout(params)
    } else {
      // scout_api might map to newly-applied
      return scoutApi.runScoutNewlyApplied(params)
    }
  },

  // Ingest a manual lead
  ingestLead: (data: IngestLeadRequest) =>
    apiClient.post<{ success: boolean; lead_id?: string }>('/api/dashboard/leads/create', {
      company_name: data.company_name,
      mc_number: data.mc_number,
      dot_number: data.dot_number,
      phone: data.phone,
      email: data.email,
      physical_city: data.city,
      physical_state: data.state,
      physical_zip: data.zip_code,
      source: data.source || 'manual',
    }),

  // Scheduler job management
  getSchedulerJobs: () =>
    apiClient.get<SchedulerJobsResponse>('/api/scout/scheduler/jobs'),

  pauseSchedulerJob: (jobName: string) =>
    apiClient.post<{ success: boolean; job_name: string; state: string; message: string }>(
      `/api/scout/scheduler/jobs/${jobName}/pause`
    ),

  resumeSchedulerJob: (jobName: string) =>
    apiClient.post<{ success: boolean; job_name: string; state: string; message: string }>(
      `/api/scout/scheduler/jobs/${jobName}/resume`
    ),

  runSchedulerJobNow: (jobName: string) =>
    apiClient.post<{ success: boolean; job_name: string; message: string }>(
      `/api/scout/scheduler/jobs/${jobName}/run`
    ),

  deleteSchedulerJob: (jobName: string) =>
    apiClient.delete<{ success: boolean; job_name: string; message: string }>(
      `/api/scout/scheduler/jobs/${jobName}`
    ),

  getJobResults: (jobName: string) =>
    apiClient.get<JobResultsResponse>(`/api/scout/scheduler/jobs/${jobName}/results`),
}
